/**
 * API helper for admin authentication and requests
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Token storage
const TOKEN_KEY = 'escrow_admin_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Auth check
export const isAuthenticated = () => {
  const token = getToken();
  if (!token) return false;
  
  // Basic JWT expiry check
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

// Admin login
export const adminLogin = async (username, password) => {
  const response = await fetch(`${BACKEND_URL}/api/escrow-admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  const text = await response.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { detail: text || 'Login failed' };
  }

  if (!response.ok) {
    throw new Error(data.detail || data.message || 'Login failed');
  }

  setToken(data.access_token);
  return data;
};

// Admin logout
export const adminLogout = () => {
  clearToken();
};

// Authenticated fetch helper
export const authFetch = async (url, options = {}) => {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${BACKEND_URL}${url}`, {
    ...options,
    headers,
  });
  
  if (response.status === 401) {
    clearToken();
    window.location.href = '/admin/login';
    throw new Error('Session expired');
  }
  
  return response;
};

// API methods
export const api = {
  // Auth
  verifyToken: async () => {
    const res = await authFetch('/api/escrow-admin/verify');
    return res.json();
  },
  
  // Manual Checkouts
  listCheckouts: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await authFetch(`/api/escrow-admin/manual-checkouts?${query}`);
    return res.json();
  },
  
  createCheckout: async (data) => {
    const res = await authFetch('/api/escrow-admin/manual-checkouts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.json();
  },
  
  getCheckout: async (id) => {
    const res = await authFetch(`/api/escrow-admin/manual-checkouts/${id}`);
    return res.json();
  },
  
  updateCheckout: async (id, data) => {
    const res = await authFetch(`/api/escrow-admin/manual-checkouts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.json();
  },
  
  deleteCheckout: async (id) => {
    const res = await authFetch(`/api/escrow-admin/manual-checkouts/${id}`, {
      method: 'DELETE',
    });
    return res.json();
  },
  
  // Mailboxes
  listMailboxes: async () => {
    const res = await authFetch('/api/escrow-admin/mailboxes');
    return res.json();
  },
  
  startOAuth: async (parserType) => {
    const res = await authFetch(`/api/escrow-admin/mailboxes/oauth/start?parser_type=${parserType}`);
    return res.json();
  },
  
  updateMailbox: async (id, data) => {
    const res = await authFetch(`/api/escrow-admin/mailboxes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.json();
  },
  
  syncMailbox: async (id, force = false) => {
    const res = await authFetch(`/api/escrow-admin/mailboxes/${id}/sync?force=${force}`, {
      method: 'POST',
    });
    return res.json();
  },
  
  // Mail Events
  listMailEvents: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await authFetch(`/api/escrow-admin/mailboxes/events?${query}`);
    return res.json();
  },
  
  retryParse: async (eventId) => {
    const res = await authFetch(`/api/escrow-admin/mailboxes/events/${eventId}/retry-parse`, {
      method: 'POST',
    });
    return res.json();
  },
  
  retryMatch: async (eventId, dryRun = false) => {
    const res = await authFetch(`/api/escrow-admin/mailboxes/events/${eventId}/retry-match?dry_run=${dryRun}`, {
      method: 'POST',
    });
    return res.json();
  },
  
  approveMatch: async (eventId, orderId) => {
    const res = await authFetch(`/api/escrow-admin/mailboxes/events/${eventId}/approve?order_id=${orderId}`, {
      method: 'POST',
    });
    return res.json();
  },
  
  ignoreEvent: async (eventId) => {
    const res = await authFetch(`/api/escrow-admin/mailboxes/events/${eventId}/ignore`, {
      method: 'POST',
    });
    return res.json();
  },
  
  // Payment Claims
  listClaims: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await authFetch(`/api/escrow-admin/claims?${query}`);
    return res.json();
  },
  
  approveClaim: async (claimId, notes = '') => {
    const res = await authFetch(`/api/escrow-admin/claims/${claimId}/approve?notes=${encodeURIComponent(notes)}`, {
      method: 'POST',
    });
    return res.json();
  },
  
  rejectClaim: async (claimId, notes = '') => {
    const res = await authFetch(`/api/escrow-admin/claims/${claimId}/reject?notes=${encodeURIComponent(notes)}`, {
      method: 'POST',
    });
    return res.json();
  },

  // Gateway Clients (multi-client API keys / webhook secrets / webhook URLs)
  listClients: async () => {
    const res = await authFetch('/api/escrow-admin/clients');
    return res.json();
  },

  createClient: async (data) => {
    const res = await authFetch('/api/escrow-admin/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create client');
    }
    return res.json();
  },

  updateClient: async (id, data) => {
    const res = await authFetch(`/api/escrow-admin/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update client');
    }
    return res.json();
  },

  deleteClient: async (id) => {
    const res = await authFetch(`/api/escrow-admin/clients/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to delete client');
    }
    return res.json();
  },

  regenerateClientApiKey: async (id) => {
    const res = await authFetch(`/api/escrow-admin/clients/${id}/regenerate-api-key`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to regenerate API key');
    return res.json();
  },

  regenerateClientWebhookSecret: async (id) => {
    const res = await authFetch(`/api/escrow-admin/clients/${id}/regenerate-webhook-secret`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to regenerate webhook secret');
    return res.json();
  },

  revealClientSecret: async (id, field) => {
    const res = await authFetch(`/api/escrow-admin/clients/${id}/reveal/${field}`);
    if (!res.ok) throw new Error('Failed to reveal secret');
    return res.json();
  },

  testClientWebhook: async (id) => {
    const res = await authFetch(`/api/escrow-admin/clients/${id}/test-webhook`, {
      method: 'POST',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.detail || 'Test webhook failed');
    }
    return data;
  },

  listClientTransactions: async (id, params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await authFetch(`/api/escrow-admin/clients/${id}/transactions?${query}`);
    if (!res.ok) throw new Error('Failed to load client transactions');
    return res.json();
  },

  // Gateway Admin Console
  gatewayOverview: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await authFetch(`/api/escrow-admin/gateway/overview?${query}`);
    if (!res.ok) throw new Error('Failed to load gateway overview');
    return res.json();
  },

  gatewayTransactions: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await authFetch(`/api/escrow-admin/gateway/transactions?${query}`);
    if (!res.ok) throw new Error('Failed to load gateway transactions');
    return res.json();
  },

  gatewayTransaction: async (txId) => {
    const res = await authFetch(`/api/escrow-admin/gateway/transactions/${encodeURIComponent(txId)}`);
    if (!res.ok) throw new Error('Failed to load transaction detail');
    return res.json();
  },

  gatewayEvents: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await authFetch(`/api/escrow-admin/gateway/events?${query}`);
    if (!res.ok) throw new Error('Failed to load gateway events');
    return res.json();
  },

  gatewayMethods: async () => {
    const res = await authFetch('/api/escrow-admin/gateway/methods');
    if (!res.ok) throw new Error('Failed to load gateway methods');
    return res.json();
  },
};

// Public checkout API (no auth required)
export const publicApi = {
  getCheckout: async (token, amount = null) => {
    let url = `${BACKEND_URL}/api/checkout/${token}`;
    if (amount) {
      url += `?amount=${amount}`;
    }
    const res = await fetch(url);
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Checkout not found');
    }
    return res.json();
  },
  
  submitClaim: async (token, firstName, lastName) => {
    const res = await fetch(`${BACKEND_URL}/api/checkout/${token}/payment-successful`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ first_name: firstName, last_name: lastName }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Failed to submit claim');
    }
    return res.json();
  },
  
  getStatus: async (token) => {
    const res = await fetch(`${BACKEND_URL}/api/checkout/${token}/status`);
    return res.json();
  },
};

export default api;
